import React, {useEffect, useState} from "react";
import toast from "react-hot-toast";
import {useGuideFlowProviderContext} from "../../../../GuideFlowProviderContext";
import {classNames} from "../../../viewUtils";
import {FollowUpRecommendation} from "../../../../providers/recomendations/RecommendationProvider";
import {ContactProfileHeader} from "../../../controls/renderers/Contacts/ContactProfileHeader";

interface ClientFollowUpsProps {
}

export function ClientFollowUps(props: ClientFollowUpsProps) {
    const context = useGuideFlowProviderContext();
    const [recommendations, setRecommendations] = useState<FollowUpRecommendation[]>();
    const [loaded, setLoaded] = useState(false);
    const [workingId, setWorkingId] = useState<string | null>(null);

    const handleDismiss = async (reco: FollowUpRecommendation) => {
        setWorkingId(reco.id);
        // Optimistically remove
        setRecommendations((prev) => prev?.filter((r) => r.id !== reco.id));

        let undo = false;

        // Toast with Undo button
        toast(
            (t) => (
                <div className="flex items-center gap-3">
                    <span>Dismissed {reco.subject}</span>
                    <button
                        className="text-indigo-600 hover:underline text-sm"
                        onClick={() => {
                            undo = true;
                            toast.dismiss(t.id);
                            // Restore locally
                            setRecommendations((prev) => [...(prev || []), reco]);
                            context.Recommendations.undismiss(reco.id);
                        }}
                    >
                        Undo
                    </button>
                </div>
            ),
            {duration: 5000}
        );

        // After 5s, finalize dismiss if no undo
        setTimeout(async () => {
            if (!undo) {
                try {
                    await context.Recommendations.dismiss(reco.id);
                } catch (err) {
                    console.error("Failed to dismiss follow-up:", err);
                }
            }
        }, 5000);

        setWorkingId(null);
    };

    const handleSnooze = async (id: string, days: number) => {
        try {
            await context.Recommendations.snoozeFollowup(id, days);
            setRecommendations((prev) =>
                prev?.map((r) =>
                    r.id === id
                        ? {
                            ...r,
                            dueDate: new Date(
                                (r.dueDate ? new Date(r.dueDate) : new Date()).getTime() +
                                days * 24 * 60 * 60 * 1000
                            ).toISOString(),
                        }
                        : r
                )
            );
            toast.success(`Snoozed ${days} day${days > 1 ? "s" : ""}`, {icon: "😴"});
        } catch (err) {
            console.error("Failed to snooze follow-up:", err);
        }
    };

    useEffect(() => {
        context.Recommendations.getFollowUps(6).then((followupList) => {
            setRecommendations(followupList);
            setLoaded(true);
        });
    }, [props, context.Recommendations.StateChanged, context.Recommendations]);

    if (!loaded) {
        return (
            <ul className="divide-y divide-gray-100 animate-pulse">
                {Array.from({length: 3}).map((_, idx) => (
                    <li
                        key={idx}
                        className="flex flex-col sm:flex-row sm:justify-between gap-4 py-5"
                    >
                        <div className="flex min-w-0 gap-x-4">
                            <div className="h-10 w-10 rounded-full bg-gray-200"/>
                            <div className="flex flex-col justify-center">
                                <div className="h-4 w-24 bg-gray-200 rounded mb-2"/>
                                <div className="h-3 w-40 bg-gray-100 rounded"/>
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-x-2 sm:gap-x-4">
                            <div className="h-6 w-12 bg-gray-200 rounded"/>
                            <div className="h-6 w-20 bg-gray-200 rounded"/>
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    if (!recommendations || recommendations.length === 0) {
        return (
            <div className="p-4 text-sm text-gray-500">
                No clients need follow-up right now 🎉
            </div>
        );
    }
    
    return (
        <ul className="divide-y divide-gray-100">
            {recommendations.map((reco) => {
                const isWorking = workingId === reco.id;
                const target = reco.targetUrl ?? `/contacts/${reco.contactId}`;
                
                return (
                    <li
                        key={reco.id}
                        className="flex flex-col sm:flex-row sm:justify-between gap-4 py-5"
                    >
                        <div className="flex min-w-0 gap-x-4">
                            <ContactProfileHeader id={reco.contactId}/>
                            <div className="flex flex-col justify-center">
                <span
                    className={classNames(
                        "inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
                    )}
                >
                  {reco.subject}
                </span>
                                {reco.detail && (
                                    <p className="mt-1 text-xs text-gray-500">{reco.detail}</p>
                                )}
                                {reco.dueDate && (
                                    <p className="mt-0.5 text-xs text-gray-400">
                                        Due: {new Date(reco.dueDate).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-x-2 sm:gap-x-4">
                            <button
                                onClick={() => handleDismiss(reco)}
                                disabled={isWorking}
                                className={classNames(
                                    "text-sm",
                                    isWorking
                                        ? "text-gray-400 cursor-not-allowed"
                                        : "text-gray-500 hover:text-red-600"
                                )}>
                                {isWorking ? "Working..." : "Dismiss"}
                            </button>
                            {/*<DropMenu imageElement={<div>Actions</div>}>*/}
                            {/*    <DropMenuItem title="View" to={target}/>*/}
                            {/*    <DropMenuItem*/}
                            {/*        title="Snooze 1 day"*/}
                            {/*        onClick={() => handleSnooze(reco.id, 1)}*/}
                            {/*    />*/}
                            {/*    <DropMenuItem*/}
                            {/*        title="Snooze 3 days"*/}
                            {/*        onClick={() => handleSnooze(reco.id, 3)}*/}
                            {/*    />*/}
                            {/*    <DropMenuItem*/}
                            {/*        title="Snooze 7 days"*/}
                            {/*        onClick={() => handleSnooze(reco.id, 7)}*/}
                            {/*    />*/}
                            {/*    <DropMenuItem*/}
                            {/*        title="Snooze 1 month"*/}
                            {/*        onClick={() => handleSnooze(reco.id, 30)}*/}
                            {/*    />*/}
                            {/*</DropMenu>*/}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
